import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Eye, ExternalLink, Globe, User } from 'lucide-react';
import { useVisitorDetail } from '@/hooks/useTracking';
import { Card, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { PageView } from '@/types';

function formatDuration(seconds?: number) {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function PageViewRow({ pv }: { pv: PageView }) {
  return (
    <div className="flex items-center gap-3 py-2.5 px-3 hover:bg-grey-50 rounded-lg transition-colors">
      <div className="w-8 h-8 rounded-full bg-grey-100 flex items-center justify-center shrink-0">
        <Globe className="w-3.5 h-3.5 text-grey-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-secondary truncate">
          {pv.title || pv.path}
        </p>
        <p className="text-xs text-grey-400 truncate">{pv.url}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0 text-xs text-grey-500">
        {pv.duration != null && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDuration(pv.duration)}
          </span>
        )}
        <span>{new Date(pv.createdAt).toLocaleTimeString()}</span>
        <a
          href={pv.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-grey-400 hover:text-accent"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

export default function VisitorDetail() {
  const { visitorId } = useParams<{ visitorId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useVisitorDetail(visitorId);

  if (isLoading) {
    return (
      <div className="page-enter space-y-6">
        <Skeleton className="h-12 w-1/3 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-enter text-center py-12">
        <p className="text-grey-500">Visitor not found</p>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/admin/visitors')}
          className="p-2 rounded-lg hover:bg-grey-100 transition-colors"
          aria-label="Back to visitors"
        >
          <ArrowLeft className="w-5 h-5 text-grey-500" />
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              data.contact ? 'bg-accent/10' : 'bg-grey-200'
            }`}
          >
            {data.contact ? (
              <User className="w-5 h-5 text-accent" />
            ) : (
              <Eye className="w-5 h-5 text-grey-400" />
            )}
          </div>
          <div>
            {data.contact ? (
              <>
                <Link
                  to={`/admin/contacts/${data.contact.id}`}
                  className="text-xl font-bold text-accent hover:underline"
                >
                  {data.contact.firstName} {data.contact.lastName}
                </Link>
                {data.contact.email && (
                  <p className="text-sm text-grey-500">{data.contact.email}</p>
                )}
              </>
            ) : (
              <h1 className="text-xl font-bold text-secondary font-mono">
                {data.visitorId}
              </h1>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card padding="md">
          <p className="text-xs font-medium text-grey-500 uppercase tracking-wider">Page Views</p>
          <p className="text-2xl font-bold text-secondary mt-1 num">{data.totalViews}</p>
        </Card>
        <Card padding="md">
          <p className="text-xs font-medium text-grey-500 uppercase tracking-wider">Sessions</p>
          <p className="text-2xl font-bold text-secondary mt-1 num">{data.totalSessions}</p>
        </Card>
        <Card padding="md">
          <p className="text-xs font-medium text-grey-500 uppercase tracking-wider">Visitor ID</p>
          <p className="text-sm font-mono text-grey-600 mt-2 truncate">{data.visitorId}</p>
        </Card>
      </div>

      {/* Sessions */}
      {data.sessions.map((session) => (
        <Card key={session.sessionId} padding="md">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Badge variant="default" size="sm">
                {session.views.length} {session.views.length === 1 ? 'page' : 'pages'}
              </Badge>
              <span className="text-xs text-grey-500">
                {new Date(session.startedAt).toLocaleDateString()} at{' '}
                {new Date(session.startedAt).toLocaleTimeString()}
              </span>
            </div>
            <span className="text-xs font-mono text-grey-400">
              {session.sessionId.slice(0, 12)}...
            </span>
          </div>
          <div className="divide-y divide-grey-50">
            {session.views.map((pv) => (
              <PageViewRow key={pv.id} pv={pv} />
            ))}
          </div>
        </Card>
      ))}

      {data.sessions.length === 0 && (
        <Card padding="md">
          <div className="text-center py-8">
            <Globe className="w-10 h-10 text-grey-300 mx-auto mb-2" />
            <p className="text-grey-500">No page views recorded yet</p>
          </div>
        </Card>
      )}
    </div>
  );
}
